import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, FileText, Bell, Users } from 'lucide-react';
import useSeo from '../hooks/useSeo';
import FaqSection from '../components/FaqSection';

/**
 * SEO landing page targeting "airbnb host only fee uk alternative" and
 * adjacent phrases (low competition, riding the Oct 2025 Airbnb UK host-fee
 * change to ~18% with VAT). Static, content-rich, single clear CTA to
 * /become-host. Per-page title/description/OG come from the shared useSeo hook;
 * crawlers see them because this route is prerendered at build time
 * (frontend/scripts/prerender.js).
 */

const FAQ = [
  { question: "What is the Airbnb host only fee in the UK?", answer: "The host only fee is the model where Airbnb takes its whole commission from you, the host, instead of splitting it with the guest. For many UK hosts that now works out at roughly 15 to 18 percent of each booking once VAT is added, so on a £1,000 stay you lose around £150 to £180 before any of your own costs." },
  { question: "How much commission does Ariv charge hosts?", answer: "Ariv charges a 13 percent host commission on accommodation, and because it is host only the guest pays no separate fee, which keeps it below Airbnb's roughly 15 to 18 percent. The first 100 founding hosts pay 0 percent for their first 90 days, then 10 percent for the rest of year one. Traditional full management companies take 15 to 30 percent, so you keep far more either way." },
  { question: "Can I use Ariv and Airbnb at the same time?", answer: "Yes. There is no exclusivity and no lock in, so you can list on Ariv alongside Airbnb and any other platform while you see how the bookings come in. Every stay that comes through Ariv simply keeps more money with you." },
  { question: "Does Ariv provide cleaners for my Airbnb in my city?", answer: "We are building our network of local cleaners and tradespeople city by city, and as the network launches we will ID and background check them, so availability depends on where your property is. List your place and we will tell you honestly what cover we have in your area, and you can keep self managing changeovers in the meantime." },
  { question: "How do I switch my short stay property to Ariv?", answer: "Create a free host account, verify your identity, then add your property, photos and pricing, which takes about fifteen minutes. Connect your payout details and go live, all at /become-host, while cleaners and tradespeople can join the network at /provider/signup." },
];

const SERVICE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Low commission short stay hosting on Ariv",
  serviceType: "Short let and Airbnb hosting platform",
  provider: { "@type": "Organization", name: "Ariv", legalName: "Brick & Capital Ltd", url: "https://ariv.co.uk/" },
  areaServed: { "@type": "Country", name: "United Kingdom" },
  url: "https://ariv.co.uk/airbnb-host-fee-alternative/",
};
const JSONLD = [SERVICE_JSONLD];

const Section = ({ id, title, children }) => (
  <section id={id} className="max-w-3xl mx-auto px-6 py-8 border-t border-gray-100">
    <h2 className="text-2xl font-bold text-[#1F3A68] mb-3">{title}</h2>
    <div className="text-gray-700 leading-relaxed space-y-3">{children}</div>
  </section>
);

const HostFeeAlternativePage = () => {
  useSeo({
    title: "Airbnb 18% host fee? A lower commission UK alternative | Ariv",
    description: "Airbnb's UK host only fee now costs around 18% with VAT. See how Ariv cuts your commission, with local cleaners and clear monthly statements. Compare today.",
    canonical: "https://ariv.co.uk/airbnb-host-fee-alternative/",
    image: "https://ariv.co.uk/icons/icon-512.png",
    imageAlt: "Ariv",
    faq: FAQ,
    jsonLd: JSONLD,
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></Link>
          <Link to="/become-host" className="bg-[#1F3A68] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1F3A68]/90">List your place</Link>
        </div>
      </nav>

      {/* Hero */}
      <header className="bg-gradient-to-br from-[#F4F2E9] to-white">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <p className="inline-block text-xs font-semibold text-[#CB3B3B] bg-[#CB3B3B]/10 px-3 py-1 rounded-full mb-4">For UK short stay hosts</p>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1F3A68] mb-4 leading-tight">
            Paying Airbnb's 18% host fee? Here is a lower commission UK alternative
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            Since late 2025 many UK hosts pay an Airbnb host only fee of around 15 to 18 percent once VAT is added. Ariv was built so you keep more of every booking, with local cleaners and trades and clear monthly statements in one place.
          </p>
          <Link to="/become-host" className="inline-flex items-center gap-2 mt-6 bg-[#1F3A68] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#1F3A68]/90">
            See how much you would keep <ArrowRight size={18} />
          </Link>
        </div>
      </header>

      <Section id="cost" title="What the Airbnb host only fee actually costs you">
        <p>
          Airbnb moved many UK hosts onto a host only fee model, where the platform takes its cut from you rather than splitting it with the guest. Once VAT is added, that lands at roughly 15 to 18 percent of each booking, depending on your setup.
        </p>
        <p>
          On a £1,000 booking that is £150 to £180 gone before you have paid for cleaning, supplies, or your own time. Across a busy month it adds up to real money that never reaches you.
        </p>
      </Section>

      <Section id="compare" title="How Ariv compares (a worked example)">
        <p>Here is the same £1,000 booking, side by side.</p>
        <div className="grid sm:grid-cols-2 gap-4 my-4">
          <div className="border border-gray-200 rounded-xl p-5">
            <p className="text-sm font-semibold text-gray-500 uppercase mb-1">Airbnb host only fee</p>
            <p className="text-3xl font-bold text-[#CB3B3B]">about £165</p>
            <p className="text-sm text-gray-500 mt-1">taken from your £1,000 booking</p>
          </div>
          <div className="border-2 border-[#1F3A68] rounded-xl p-5 bg-[#1F3A68]/5">
            <p className="text-sm font-semibold text-[#1F3A68] uppercase mb-1">Ariv founding host</p>
            <p className="text-3xl font-bold text-[#1F3A68]">£0</p>
            <p className="text-sm text-gray-500 mt-1">for your first 90 days, then 10% for year one, then 13%</p>
          </div>
        </div>
        <p>
          As one of the first 100 founding hosts you pay nothing for 90 days, then 10% for the rest of year one, then a standard 13% which is still below Airbnb's 15 to 18%. And because Ariv is host only, the guest pays no separate service fee, so your all in price stays simple and competitive.
        </p>
      </Section>

      <Section id="included" title="What is included: cleaners, trades, statements">
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { icon: Users, t: 'Cleaning and trades, built around your bookings', d: 'Book a changeover cleaner or a tradesperson without scrambling at the last minute. As the network launches we will ID and background check them, so you see who is coming, not random subcontractors.' },
            { icon: FileText, t: 'Clear monthly statements', d: 'See exactly what you earned and what was deducted, in plain English, without building your own spreadsheet.' },
            { icon: Bell, t: 'Live stock alerts', d: 'Get told when the coffee, toilet roll, or linen is running low at a property, before a guest finds out the hard way.' },
            { icon: Shield, t: 'UK compliance built in', d: 'Tools that help you stay on the right side of UK short let rules, with your data encrypted and handled under UK GDPR.' },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="border border-gray-200 rounded-xl p-5">
              <Icon size={22} className="text-[#1F3A68] mb-2" />
              <p className="font-bold text-gray-900 mb-1">{t}</p>
              <p className="text-sm text-gray-600">{d}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm text-gray-600">
          Cleaning matters as much as the booking itself. See how we are building our{' '}
          <Link to="/airbnb-cleaning/" className="text-[#1F3A68] underline">local cleaner network city by city</Link>, or join as a cleaner or tradesperson at{' '}
          <Link to="/provider/signup" className="text-[#1F3A68] underline">/provider/signup</Link>.
        </p>
      </Section>

      <Section id="bookings" title="Will I lose bookings if I switch?">
        <p>
          You do not have to choose one or the other. Most hosts list on more than one platform, and you can run Ariv alongside Airbnb while you see how the bookings come in. There is no exclusivity and no lock in.
        </p>
        <p>
          The point is simple: every booking that comes through Ariv keeps far more money with you, so even a handful of stays a month makes a real difference to what you take home.
        </p>
      </Section>

      <Section id="switch" title="How it works, step by step">
        <ol className="list-decimal list-inside space-y-2">
          <li>Create your free host account and verify your identity.</li>
          <li>Add your property, photos, and pricing. It takes about fifteen minutes.</li>
          <li>Connect your payout details so you get paid directly.</li>
          <li>Go live and start taking bookings at a fraction of the commission.</li>
        </ol>
        <div className="mt-6">
          <Link to="/become-host" className="inline-flex items-center gap-2 bg-[#1F3A68] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#1F3A68]/90">
            List your place on Ariv <ArrowRight size={18} />
          </Link>
        </div>
      </Section>

      <Section id="trust" title="Is Ariv a real business?">
        <p>
          Yes. Ariv is operated by Brick &amp; Capital Ltd, registered in England and Wales (company number 16655788) and on the ICO data protection register (ZC129399). You can read more on our{' '}
          <Link to="/trust" className="text-[#1F3A68] underline">Trust and Safety page</Link>, or see the full picture on{' '}
          <Link to="/how-it-works" className="text-[#1F3A68] underline">how it works</Link>.
        </p>
      </Section>

      <FaqSection faq={FAQ} />

      {/* Footer */}
      <footer className="bg-[#1F3A68] text-white py-8 mt-8">
        <div className="max-w-3xl mx-auto px-6 text-center text-white/70 text-sm">
          <p>&copy; {new Date().getFullYear()} Ariv. A product of Brick &amp; Capital Ltd.</p>
          <p className="text-xs mt-2 text-white/50">Registered in England and Wales, company number 16655788. Registered office: Bowness House, 815 Old Kent Road, London SE15 1NX.</p>
          <div className="flex flex-wrap gap-4 justify-center mt-3">
            <Link to="/become-host" className="hover:text-white">List your place</Link>
            <Link to="/low-commission-airbnb-management/" className="hover:text-white">Low commission management</Link>
            <Link to="/trust" className="hover:text-white">Trust &amp; Safety</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HostFeeAlternativePage;
